import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  CubeIcon,
  PhotoIcon,
  WrenchScrewdriverIcon,
  WalletIcon,
} from '@heroicons/react/24/outline';
import { useWalletStore } from '../store/walletStore';
import { listTokens, getBalance, listNftsByOwner } from '../api/vexforge';

const TABS = [
  { id: 'fungible', label: 'Fungible', icon: CubeIcon },
  { id: 'nfts', label: 'NFTs', icon: PhotoIcon },
  { id: 'created', label: 'Created', icon: WrenchScrewdriverIcon },
];

export default function MyTokens() {
  const { address, isConnected } = useWalletStore();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'fungible');
  const [tokens, setTokens] = useState([]);
  const [balances, setBalances] = useState({});
  const [nfts, setNfts] = useState([]);
  const [createdTokens, setCreatedTokens] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isConnected || !address) return;

    async function fetchData() {
      setLoading(true);
      try {
        const allTokens = await listTokens(100);
        const tokenList = Array.isArray(allTokens) ? allTokens : [];
        setTokens(tokenList);

        // Fetch balances for each token
        const balMap = {};
        for (const t of tokenList) {
          const symbol = t.symbol;
          if (symbol) {
            try {
              const bal = await getBalance(address, symbol);
              if (bal && bal !== '0') balMap[symbol] = bal;
            } catch {
              // Skip
            }
          }
        }
        setBalances(balMap);

        // Fetch NFTs
        try {
          const nftResult = await listNftsByOwner(address, null, 50);
          setNfts(Array.isArray(nftResult) ? nftResult : []);
        } catch {
          setNfts([]);
        }

        // Filter created tokens
        setCreatedTokens(tokenList.filter(t =>
          t.creator && t.creator.toLowerCase() === address.toLowerCase()
        ));
      } catch (err) {
        console.error('Failed to fetch tokens:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [address, isConnected]);

  if (!isConnected) {
    return (
      <div className="text-center py-16">
        <WalletIcon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">Connect Your Wallet</h1>
        <p className="text-gray-400">Connect your wallet to see your tokens and NFTs</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white">My Tokens</h1>
        <p className="text-gray-400 mt-1 font-mono text-sm">{address}</p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-vextaris-dark/50 rounded-xl border border-vextaris-purple/20 p-1">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium flex-1 justify-center transition-colors ${
                activeTab === tab.id
                  ? 'bg-vextaris-purple text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : (
        <>
          {/* Fungible Tab */}
          {activeTab === 'fungible' && (
            <div className="space-y-3">
              {Object.entries(balances).length === 0 ? (
                <div className="bg-vextaris-dark/50 rounded-2xl border border-vextaris-purple/20 p-12 text-center">
                  <p className="text-gray-400">No token balances found</p>
                </div>
              ) : (
                Object.entries(balances).map(([symbol, bal]) => {
                  const token = tokens.find(t => t.symbol === symbol);
                  const decimals = token?.decimals || 9;
                  const humanBal = (Number(bal) / Math.pow(10, decimals));
                  const displayBal = humanBal >= 1000000 ? (humanBal / 1000000).toFixed(2) + 'M' :
                                     humanBal >= 1000 ? humanBal.toLocaleString(undefined, { maximumFractionDigits: 2 }) :
                                     humanBal.toLocaleString(undefined, { maximumFractionDigits: 4 });
                  return (
                    <div key={symbol} className="bg-vextaris-dark/50 backdrop-blur-xl rounded-xl border border-vextaris-purple/20 p-4 flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        {token?.image ? (
                          <img src={token.image} alt={symbol} className="w-10 h-10 rounded-lg" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-gradient-vextaris flex items-center justify-center text-sm font-bold text-white">
                            {symbol[0]}
                          </div>
                        )}
                        <div>
                          <p className="text-white font-medium">{token?.name || symbol}</p>
                          <p className="text-xs text-gray-400 font-mono">{symbol}</p>
                        </div>
                      </div>
                      <p className="text-white font-semibold">{displayBal}</p>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* NFTs Tab */}
          {activeTab === 'nfts' && (
            <div>
              {nfts.length === 0 ? (
                <div className="bg-vextaris-dark/50 rounded-2xl border border-vextaris-purple/20 p-12 text-center">
                  <p className="text-gray-400">No NFTs found</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {nfts.map((nft, i) => (
                    <div key={i} className="bg-vextaris-dark/50 backdrop-blur-xl rounded-xl border border-vextaris-purple/20 overflow-hidden hover:border-vextaris-purple/40 transition-colors">
                      {nft.metadata_uri ? (
                        <div className="aspect-square bg-vextaris-darker flex items-center justify-center">
                          <PhotoIcon className="w-12 h-12 text-gray-600" />
                        </div>
                      ) : (
                        <div className="aspect-square bg-gradient-vextaris flex items-center justify-center">
                          <span className="text-4xl font-bold text-white">#{nft.token_id}</span>
                        </div>
                      )}
                      <div className="p-3">
                        <p className="text-white text-sm font-medium truncate">Token #{nft.token_id}</p>
                        <p className="text-xs text-gray-400 font-mono truncate">{nft.collection}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Created Tab */}
          {activeTab === 'created' && (
            <div className="space-y-3">
              {createdTokens.length === 0 ? (
                <div className="bg-vextaris-dark/50 rounded-2xl border border-vextaris-purple/20 p-12 text-center">
                  <p className="text-gray-400">You haven't created any tokens yet</p>
                  <Link to="/create" className="text-vextaris-blue hover:text-white mt-2 inline-block text-sm">
                    Create your first token
                  </Link>
                </div>
              ) : (
                createdTokens.map((token, i) => {
                  const mint = token.mint_address || token.mint_address_hex || '';
                  return (
                    <Link
                      key={i}
                      to={`/token/${mint}`}
                      className="block bg-vextaris-dark/50 backdrop-blur-xl rounded-xl border border-vextaris-purple/20 p-4 hover:border-vextaris-purple/40 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-vextaris flex items-center justify-center text-sm font-bold text-white">
                            {(token.symbol || '?')[0]}
                          </div>
                          <div>
                            <p className="text-white font-medium">{token.name}</p>
                            <p className="text-xs text-gray-400 font-mono">{token.symbol}</p>
                          </div>
                        </div>
                        <p className="text-gray-400 text-sm">
                          {token.total_supply ? (Number(token.total_supply) / Math.pow(10, token.decimals || 9)).toLocaleString(undefined, { maximumFractionDigits: 0 }) : '--'} supply
                        </p>
                      </div>
                    </Link>
                  );
                })
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}